/* Cotton Purchases — the bills register read back: bales bought, the rate
 * paid, and who supplied them.
 *
 * A port of `Cotton Purchases.dc.html`. One row is one bill line; every
 * figure is a sum over the lines in the selection. The one departure: the
 * design converts ₹/kg to ₹/candy at 355.62 kg, the app at 356 — the one
 * candy the rest of the codebase uses (see services/units.py).
 */

import { useMemo, useRef, useState, type CSSProperties } from 'react'
import { MAIN_STYLE, Subline } from '../App'
import { purchases } from '../lib/api_purchases'
import type { PurchaseRow } from '../lib/api_purchases'
import { useAsync } from '../lib/useAsync'
import { useCan } from '../lib/auth'
import { Blueprint, Empty, ErrorBox, KpiRow, LoadElsewhere, Loading, PanelHead } from '../components/ui'
import { CANDY_KG } from '../lib/cost'
import { num } from '../lib/format'

const R: CSSProperties = { textAlign: 'right' }
const MN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const LIM = 20

type Type = PurchaseRow['type']
const TYPES: { k: Type; label: string; bg: string }[] = [
  { k: 'CCI', label: 'CCI', bg: 'var(--color-accent-700)' },
  { k: 'Private', label: 'Private ginners & traders', bg: 'var(--color-accent-400)' },
  { k: 'Group', label: 'Group (Mohan Spintex)', bg: 'var(--color-neutral-500)' },
]
const T_BG: Record<Type, string> = { CCI: TYPES[0].bg, Private: TYPES[1].bg, Group: TYPES[2].bg }
const HOVER_BG = 'color-mix(in oklch, var(--color-accent-200) 35%, transparent)'

// ---------------------------------------------------------------- format
const cr = (v: number) => '₹' + num(v / 1e7, 2) + ' Cr'
const mLabel = (k: string, long = false) => {
  const [y, m] = k.split('-')
  return MN[+m - 1] + (long ? ' 20' : ' ’') + y.slice(2)
}
const dLabel = (d: string | null) => {
  if (!d) return '–'
  const [y, m, dd] = d.split('-')
  return +dd + ' ' + MN[+m - 1] + ' ’' + y.slice(2)
}
/** A round step and top for an axis up to `max`. */
function nice(max: number, steps = 4) {
  const raw = max / steps, p = Math.pow(10, Math.floor(Math.log10(raw || 1))), f = raw / p
  const step = (f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10) * p
  return { step, top: Math.ceil(max / step) * step }
}

interface Agg {
  n: number; bales: number; kg: number; tax: number; gst: number; val: number; fr: number
  diff: number; party: number; shortN: number; pkg: number | null; candy: number | null
}
function agg(rs: PurchaseRow[]): Agg {
  const o = { n: rs.length, bales: 0, kg: 0, tax: 0, gst: 0, val: 0, fr: 0, diff: 0, party: 0, shortN: 0 }
  rs.forEach((r) => {
    o.bales += r.bales; o.kg += r.kg; o.tax += r.taxable; o.gst += r.gst; o.val += r.invVal
    o.fr += r.fr || 0
    if (r.diff !== null && r.party) { o.diff += r.diff; o.party += r.party; if (r.diff < 0) o.shortN++ }
  })
  const pkg = o.kg ? o.tax / o.kg : null
  return { ...o, pkg, candy: pkg ? pkg * CANDY_KG : null }
}
const candyOf = (r: PurchaseRow) => r.taxable / r.kg * CANDY_KG

export default function Purchases() {
  const { data, error, loading, reload } = useAsync(() => purchases.rows(), [])
  const [period, setPeriod] = useState('fy')
  const [supplier, setSupplier] = useState('all')
  const [mHov, setMHov] = useState<string | null>(null)
  const [dHov, setDHov] = useState<number | null>(null)
  const [kHov, setKHov] = useState<number | null>(null)
  const [kSrc, setKSrc] = useState<'all' | Type>('all')
  const [showAll, setShowAll] = useState(false)

  const all = useMemo(() => (data?.rows ?? []).filter((r) => r.kg > 0), [data])

  if (error) return <main style={MAIN_STYLE}><ErrorBox error={error} onRetry={reload} /></main>
  if (loading && !data) return <main style={MAIN_STYLE}><Loading what="the purchase bills" /></main>
  if (!all.length) {
    return (
      <main style={MAIN_STYLE}>
        <Empty title="No purchase bills on file yet">
          Load the cotton purchase bills workbook with <code>app.cli load-purchases FILE.xlsx</code> or
          the button below and this fills in.
        </Empty>
        <UploadLine onLoaded={reload} />
      </main>
    )
  }

  // ---- the selection --------------------------------------------------
  const mKeys = [...new Set(all.map((r) => r.mk))].sort()
  const supRowsAll = supplier === 'all' ? all : all.filter((r) => r.sup === supplier)
  const inP = (r: PurchaseRow) => period === 'fy' || r.mk === period
  const Rs = supRowsAll.filter(inP)
  const A = agg(Rs)
  const lastM = mKeys[mKeys.length - 1]
  const lastDate = all.reduce((m, r) => (r.d > m ? r.d : m), '')

  const cci = agg(Rs.filter((r) => r.type === 'CCI')), pvt = agg(Rs.filter((r) => r.type === 'Private'))
  const kpis = [
    { label: 'Bales received', value: num(A.bales), note: num(A.n) + ' invoices · ' + num(A.kg / 1000, 1) + ' t' },
    { label: 'Average rate', value: num(A.candy), unit: '₹/candy',
      note: '₹' + num(A.pkg, 2) + '/kg ex-GST, incl. CCI charges' },
    { label: 'Landed cost incl. freight', value: A.kg ? num((A.tax + A.fr) / A.kg, 2) : '–', unit: '₹/kg',
      note: A.fr ? 'Freight ₹' + num(A.fr / 1e5, 2) + ' L on recorded loads' : 'No freight recorded for this selection' },
    { label: 'Invoice value', value: num(A.val / 1e7, 2), unit: 'Cr', note: 'incl. GST ₹' + num(A.gst / 1e5, 1) + ' L' },
    { label: 'Mill − party weight', value: (A.diff > 0 ? '+' : '') + num(A.diff), unit: 'kg',
      note: A.party ? num(A.diff / A.party * 100, 2) + '% of party weight · ' + A.shortN + ' loads short' : 'No weighbridge data' },
    { label: 'CCI share', value: A.bales ? num(cci.bales / A.bales * 100, 0) : '–', unit: '% of bales',
      note: cci.n && pvt.n ? 'CCI ₹' + num(cci.candy) + ' vs private ₹' + num(pvt.candy) + ' /candy'
        : cci.n ? 'All CCI' : 'No CCI bales' },
  ]

  // ---- bales and rate by month ------------------------------------------
  const mAgg = mKeys.map((k) => { const rs = supRowsAll.filter((r) => r.mk === k); return { k, rs, a: agg(rs) } })
  const bt = nice(Math.max(1, ...mAgg.map((o) => o.a.bales)))
  const rates = mAgg.map((o) => o.a.candy).filter((v): v is number => !!v)
  const rLo = rates.length ? Math.floor((Math.min(...rates) - 3000) / 2000) * 2000 : 0
  const rHi = rates.length ? Math.ceil((Math.max(...rates) + 1500) / 2000) * 2000 : 1
  const ry = (v: number) => (v - rLo) / (rHi - rLo) * 100
  const nm = mKeys.length || 1
  const months = mAgg.map((o) => ({
    k: o.k, label: mLabel(o.k), h: o.a.bales / bt.top * 100,
    segs: TYPES.map((t) => {
      const b = o.rs.filter((r) => r.type === t.k).reduce((s, r) => s + r.bales, 0)
      return { h: o.a.bales ? b / o.a.bales * 100 : 0, bg: t.bg, b }
    }).filter((s) => s.b > 0),
    op: period === 'fy' || period === o.k ? 1 : 0.35, fw: period === o.k ? 700 : 400,
  }))
  const pts = mAgg.map((o, i) => (o.a.candy ? { x: (i + 0.5) / nm * 100, y: ry(o.a.candy), v: o.a.candy } : null))
    .filter((p): p is NonNullable<typeof p> => p !== null)
  const rateLine = pts.map((p) => p.x.toFixed(2) + ',' + (100 - p.y).toFixed(2)).join(' ')
  const mTicksL = Array.from({ length: Math.round(bt.top / bt.step) + 1 }, (_, i) => ({
    pos: i * bt.step / bt.top * 100 + '%', label: num(i * bt.step),
  }))
  const mTicksR: { pos: string; label: string }[] = []
  for (let v = rLo; v <= rHi + 1; v += (rHi - rLo) / 4) mTicksR.push({ pos: ry(v) + '%', label: '₹' + num(v / 1000, 1) + 'k' })
  const hi = mAgg.findIndex((o) => o.k === mHov)
  const monthHov = hi >= 0 ? (() => {
    const o = mAgg[hi]
    return {
      title: mLabel(o.k, true), rate: o.a.candy ? '₹' + num(o.a.candy) + ' /candy' : '–',
      sub: num(o.a.bales) + ' bales · ' + num(o.a.kg / 1000, 1) + ' t · ' + cr(o.a.val) + ' incl. GST'
        + (o.k === lastM ? ' · to ' + dLabel(lastDate) : ''),
      left: (hi + 0.5) / nm * 100 + '%', shift: hi / nm > 0.6 ? 'translateX(calc(-100% - 12px))' : 'translateX(12px)',
      rows: TYPES.map((t) => { const a = agg(o.rs.filter((r) => r.type === t.k)); return a.n ? { bg: t.bg, label: t.label, bales: num(a.bales) + ' bales', rate: '₹' + num(a.candy) } : null })
        .filter((r): r is NonNullable<typeof r> => r !== null),
    }
  })() : null

  // ---- ₹/kg by month ------------------------------------------------------
  const kSeries = [
    { k: 'all' as const, label: 'All purchases', color: 'var(--color-text)', w: 2.5, dash: '0', style: 'solid' },
    ...TYPES.map((t) => ({ k: t.k as 'all' | Type, label: t.label, color: t.bg, w: 1.5, dash: '5 4', style: 'dashed' })),
  ]
    .map((s) => ({ ...s, vals: mAgg.map((o) => { const a = s.k === 'all' ? o.a : agg(o.rs.filter((r) => r.type === s.k)); return a.n ? a.pkg : null }) }))
    .filter((s) => s.vals.some((v) => v !== null))
    .filter((s) => kSrc === 'all' || s.k === kSrc)
    .map((s) => (kSrc === 'all' ? s : { ...s, w: 2.5, dash: '0', style: 'solid' }))
  const kv = kSeries.flatMap((s) => s.vals).filter((v): v is number => v !== null)
  const kLo = kv.length ? Math.floor((Math.min(...kv) - 5) / 10) * 10 : 0
  const kHi = kv.length ? Math.ceil((Math.max(...kv) + 5) / 10) * 10 : 1
  const ky = (v: number) => (v - kLo) / (kHi - kLo) * 100
  const kStep = (kHi - kLo) / 10 > 4 ? 20 : 10
  const kTicks: { pos: string; label: string }[] = []
  for (let v = kLo; v <= kHi; v += kStep) kTicks.push({ pos: ky(v) + '%', label: '₹' + num(v) })
  const kx = (i: number) => (i + 0.5) / nm * 100
  const kLines = kSeries.map((s) => ({
    ...s, pts: s.vals.map((v, i) => (v === null ? null : kx(i).toFixed(2) + ',' + (100 - ky(v)).toFixed(2))).filter(Boolean).join(' '),
  }))
  const kDots = kSeries.flatMap((s) => s.vals.map((v, i) => (v === null ? null : { left: kx(i) + '%', bottom: ky(v) + '%', color: s.color })))
    .filter((p): p is NonNullable<typeof p> => p !== null)
  const kAll = kSeries.find((s) => s.k === 'all') || kSeries[0]
  const kAllLabels = kAll ? kAll.vals.map((v, i) => (v === null ? null : { left: kx(i) + '%', bottom: ky(v) + '%', label: '₹' + num(v, 1) }))
    .filter((p): p is NonNullable<typeof p> => p !== null) : []
  const kHovBox = kHov !== null && mAgg[kHov] ? (() => {
    const o = mAgg[kHov]
    return {
      title: mLabel(o.k, true) + ' · ' + num(o.a.kg / 1000, 1) + ' t',
      left: kx(kHov) + '%', shift: kHov / nm > 0.6 ? 'translateX(calc(-100% - 12px))' : 'translateX(12px)',
      rows: [
        ...kSeries.map((s) => (s.vals[kHov] === null ? null : { color: s.color, label: s.label + ' (ex-GST)', v: '₹' + num(s.vals[kHov], 2) })),
        o.a.fr ? { color: 'transparent', label: 'Landed incl. freight', v: '₹' + num((o.a.tax + o.a.fr) / o.a.kg, 2) } : null,
        { color: 'transparent', label: 'Incl. GST', v: '₹' + num(o.a.val / o.a.kg, 2) },
      ].filter((r): r is NonNullable<typeof r> => r !== null),
    }
  })() : null

  // ---- every invoice's rate -----------------------------------------------
  const dated = supRowsAll.filter((r) => r.d)
  const t0 = mKeys.length ? Date.parse(mKeys[0] + '-01') : 0
  const [ly, lm] = (lastM || '2026-01').split('-').map(Number)
  const t1 = Date.UTC(ly, lm, 1)
  const sx = (d: string) => (Date.parse(d) - t0) / (t1 - t0) * 100
  const cs = dated.map(candyOf)
  const sLo = cs.length ? Math.floor((Math.min(...cs) - 1000) / 5000) * 5000 : 0
  const sHi = cs.length ? Math.ceil((Math.max(...cs) + 1000) / 5000) * 5000 : 1
  const sy = (v: number) => (v - sLo) / (sHi - sLo) * 100
  const sTicks: { pos: string; label: string }[] = []
  for (let v = sLo; v <= sHi; v += 5000) sTicks.push({ pos: sy(v) + '%', label: '₹' + num(v / 1000) + 'k' })
  const sMonthLines = mKeys.map((k) => ({ k, left: sx(k + '-01') + '%' }))
  const sMonthLabels = mKeys.map((k) => {
    const [y, m] = k.split('-').map(Number)
    return { k, left: (sx(k + '-01') + (Date.UTC(y, m, 1) - t0) / (t1 - t0) * 100) / 2 + '%', label: mLabel(k) }
  })
  const dots = dated.map((r, i) => {
    const c = candyOf(r), sz = Math.max(7, Math.min(14, 5 + r.bales / 20))
    return { i, left: sx(r.d) + '%', bottom: sy(c) + '%', size: sz, bg: T_BG[r.type], op: inP(r) ? (dHov === i ? 1 : 0.85) : 0.18 }
  })
  const dotHov = dHov !== null && dated[dHov] ? (() => {
    const r = dated[dHov], c = candyOf(r), x = sx(r.d), y = sy(c)
    return {
      sup: r.sup, rate: '₹' + num(c) + ' /candy',
      l1: dLabel(r.d) + ' · ' + r.st + (r.trading ? ' · trading' : ''),
      l2: num(r.bales) + ' bales · ' + num(r.kg) + ' kg · ₹' + num(r.taxable / r.kg, 2) + '/kg',
      l3: 'Invoice ' + (r.inv || '–') + (r.candy ? ' · contract ₹' + num(r.candy) + '/candy' : ''),
      left: x + '%', bottom: y + '%',
      shift: (x > 60 ? 'translate(calc(-100% - 12px),' : 'translate(12px,') + (y > 60 ? '100%)' : '-12px)'),
    }
  })() : null

  // ---- suppliers: the period applies, the supplier filter does not, so
  // another supplier can be picked from the table.
  const PR = all.filter(inP)
  const PT = PR.reduce((s, r) => s + r.bales, 0)
  const sups = [...new Set(PR.map((r) => r.sup))].map((s) => {
    const rs = PR.filter((r) => r.sup === s), a = agg(rs), c = rs.map(candyOf)
    return { s, a, type: rs[0].type, lo: Math.min(...c), hi: Math.max(...c), st: [...new Set(rs.map((r) => r.st))].join(', ') }
  }).sort((a, b) => b.a.bales - a.a.bales)
  const supTot = agg(PR)
  const diffTxt = (a: Agg) => (a.party ? (a.diff > 0 ? '+' : '') + num(a.diff) + ' kg (' + num(a.diff / a.party * 100, 2) + '%)' : '–')

  // ---- register --------------------------------------------------------------
  const reg = Rs.slice().sort((a, b) => (b.d || '').localeCompare(a.d || ''))
  const regRows = showAll ? reg : reg.slice(0, LIM)

  const supOpts = [...new Set(all.map((r) => r.sup))].sort((a, b) => a.localeCompare(b))
  const fy = data?.fy ?? ''
  const pLabel = period === 'fy' ? `FY ${fy} to date` : mLabel(period, true)
  const typeLegend = TYPES.filter((t) => supRowsAll.some((r) => r.type === t.k))

  const label: CSSProperties = {
    flex: 'none', whiteSpace: 'nowrap', fontSize: 10, letterSpacing: '0.12em',
    textTransform: 'uppercase', color: 'var(--color-neutral-600)',
  }
  const tickL: CSSProperties = { position: 'absolute', right: 0, transform: 'translateY(50%)' }
  const tickR: CSSProperties = { position: 'absolute', left: 0, transform: 'translateY(50%)' }
  const gridLine: CSSProperties = { position: 'absolute', left: 0, right: 0, borderTop: '1px solid var(--color-neutral-200)' }
  const plot: CSSProperties = {
    position: 'relative', borderLeft: '1px solid var(--color-neutral-500)', borderBottom: '1px solid var(--color-neutral-500)',
  }
  const tip: CSSProperties = {
    position: 'absolute', pointerEvents: 'none', padding: '10px 12px', background: 'var(--color-bg)',
    border: '1px solid var(--color-divider)', boxShadow: 'var(--shadow-md)', fontSize: 12, zIndex: 3,
  }
  const axisTxt: CSSProperties = { position: 'relative', fontSize: 11, color: 'var(--color-neutral-600)' }
  const legendRow: CSSProperties = { display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 14, fontSize: 12, color: 'var(--color-neutral-800)' }

  return (
    <>
      <Subline>
        {pLabel}{supplier === 'all' ? '' : ' · ' + supplier} · bills received up to {dLabel(lastDate)}
      </Subline>

      <div className="no-print" style={{
        display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', padding: '14px 32px',
        borderBottom: '1px solid var(--color-divider)',
      }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={label}>Period</span>
          <select value={period} style={{ minWidth: 170 }}
            onChange={(e) => { setPeriod(e.target.value); setShowAll(false) }}>
            <option value="fy">FY {fy}</option>
            {mKeys.slice().reverse().map((k) => (
              <option key={k} value={k}>{mLabel(k, true)}{k === lastM ? ' (partial)' : ''}</option>
            ))}
          </select>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={label}>Supplier</span>
          <select value={supplier} style={{ minWidth: 220, maxWidth: 300 }}
            onChange={(e) => { setSupplier(e.target.value); setShowAll(false) }}>
            <option value="all">All suppliers</option>
            {supOpts.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <UploadLine onLoaded={reload} source={`${all.length} bill lines on file`} />
      </div>

      <main style={{ ...MAIN_STYLE, gap: 24 }}>
        <KpiRow items={kpis} min={200} />

        {/* Bales and rate by month */}
        <Blueprint className="sheet" style={{ padding: '22px 24px' }}>
          <PanelHead title="Bales bought and average rate, month by month"
            hint="Bars: bales by source (left axis) · Line: weighted ₹/candy ex-GST (right axis)" />
          <div style={{ display: 'grid', gridTemplateColumns: '52px minmax(0,1fr) 64px', gap: 8, marginTop: 22 }}>
            <div className="num" style={{ ...axisTxt, height: 270 }}>
              {mTicksL.map((t) => <span key={t.pos} style={{ ...tickL, bottom: t.pos }}>{t.label}</span>)}
            </div>
            <div onMouseLeave={() => setMHov(null)} style={{ ...plot, height: 270, borderRight: '1px solid var(--color-neutral-500)' }}>
              {mTicksL.map((t) => <span key={t.pos} style={{ ...gridLine, bottom: t.pos }} />)}
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'flex-end' }}>
                {months.map((b) => (
                  <div key={b.k} onMouseEnter={() => setMHov(b.k)}
                    onClick={() => { setPeriod(period === b.k ? 'fy' : b.k); setShowAll(false) }}
                    style={{
                      flex: '1 1 0', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'flex-end',
                      cursor: 'pointer', background: mHov === b.k ? HOVER_BG : 'transparent',
                    }}>
                    <div style={{ width: '46%', maxWidth: 64, height: b.h + '%', display: 'flex', flexDirection: 'column-reverse', opacity: b.op }}>
                      {b.segs.map((s, i) => <div key={i} style={{ height: s.h + '%', background: s.bg, boxShadow: 'inset 0 1px 0 var(--color-bg)' }} />)}
                    </div>
                  </div>
                ))}
              </div>
              <svg viewBox="0 0 100 100" preserveAspectRatio="none"
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'visible', pointerEvents: 'none' }}>
                <polyline points={rateLine} fill="none" stroke="var(--color-text)" strokeWidth={2} vectorEffect="non-scaling-stroke" />
              </svg>
              {pts.map((p) => (
                <div key={p.x} style={{
                  position: 'absolute', left: p.x + '%', bottom: p.y + '%', transform: 'translate(-50%,50%)',
                  pointerEvents: 'none', display: 'flex', flexDirection: 'column', alignItems: 'center',
                }}>
                  <span className="num" style={{ position: 'absolute', bottom: 10, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap', background: 'var(--color-bg)', padding: '0 3px' }}>
                    ₹{num(p.v / 1000, 1)}k
                  </span>
                  <span style={{ width: 9, height: 9, display: 'block', background: 'var(--color-bg)', border: '2px solid var(--color-text)' }} />
                </div>
              ))}
              {monthHov ? (
                <div style={{ ...tip, top: 8, left: monthHov.left, transform: monthHov.shift, minWidth: 250 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontWeight: 600 }}>
                    <span>{monthHov.title}</span><span className="num">{monthHov.rate}</span>
                  </div>
                  <div style={{ color: 'var(--color-neutral-700)', margin: '2px 0 6px' }}>{monthHov.sub}</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {monthHov.rows.map((r) => (
                      <div key={r.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ width: 10, height: 10, display: 'block', flex: 'none', background: r.bg }} />
                        <span style={{ flex: 1, color: 'var(--color-neutral-700)' }}>{r.label}</span>
                        <span className="num">{r.bales}</span>
                        <span className="num" style={{ fontWeight: 600, minWidth: 78, textAlign: 'right' }}>{r.rate}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
            <div className="num" style={{ ...axisTxt, height: 270 }}>
              {mTicksR.map((t) => <span key={t.pos} style={{ ...tickR, bottom: t.pos }}>{t.label}</span>)}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '52px minmax(0,1fr) 64px', gap: 8 }}>
            <div />
            <div style={{ display: 'flex', paddingTop: 6, fontSize: 12, color: 'var(--color-neutral-700)' }}>
              {months.map((b) => <span key={b.k} style={{ flex: '1 1 0', textAlign: 'center', whiteSpace: 'nowrap', fontWeight: b.fw }}>{b.label}</span>)}
            </div>
            <div />
          </div>
          <div style={legendRow}>
            {typeLegend.map((l) => (
              <div key={l.k} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <span style={{ width: 12, height: 12, display: 'block', background: l.bg }} />{l.label}
              </div>
            ))}
            <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              <span style={{ width: 18, height: 0, display: 'block', borderTop: '2px solid var(--color-text)' }} />Avg ₹/candy
            </div>
          </div>
        </Blueprint>

        {/* Rate paid on every invoice */}
        <Blueprint className="sheet" style={{ padding: '22px 24px' }}>
          <PanelHead title="Rate paid on every invoice"
            hint={`₹/candy = taxable value ÷ kg × ${CANDY_KG}, so CCI handling charges are included · ${num(dated.length)} invoices`} />
          <div style={{ display: 'grid', gridTemplateColumns: '60px minmax(0,1fr)', gap: 8, marginTop: 22 }}>
            <div className="num" style={{ ...axisTxt, height: 280 }}>
              {sTicks.map((t) => <span key={t.pos} style={{ ...tickL, bottom: t.pos }}>{t.label}</span>)}
            </div>
            <div onMouseLeave={() => setDHov(null)} style={{ ...plot, height: 280 }}>
              {sTicks.map((t) => <span key={t.pos} style={{ ...gridLine, bottom: t.pos }} />)}
              {sMonthLines.map((t) => <span key={t.k} style={{ position: 'absolute', top: 0, bottom: 0, left: t.left, borderLeft: '1px dashed var(--color-neutral-200)' }} />)}
              {dots.map((p) => (
                <span key={p.i} onMouseEnter={() => setDHov(p.i)} style={{
                  position: 'absolute', left: p.left, bottom: p.bottom, width: p.size, height: p.size,
                  transform: 'translate(-50%,50%)', background: p.bg, border: '1px solid var(--color-bg)',
                  borderRadius: '50%', opacity: p.op, cursor: 'default',
                }} />
              ))}
              {dotHov ? (
                <div style={{ ...tip, left: dotHov.left, bottom: dotHov.bottom, transform: dotHov.shift, minWidth: 240 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontWeight: 600 }}>
                    <span>{dotHov.sup}</span><span className="num">{dotHov.rate}</span>
                  </div>
                  <div style={{ color: 'var(--color-neutral-700)', marginTop: 2 }}>{dotHov.l1}</div>
                  <div style={{ color: 'var(--color-neutral-700)', marginTop: 2 }}>{dotHov.l2}</div>
                  <div style={{ color: 'var(--color-neutral-700)', marginTop: 2 }}>{dotHov.l3}</div>
                </div>
              ) : null}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '60px minmax(0,1fr)', gap: 8 }}>
            <div />
            <div style={{ position: 'relative', height: 20, fontSize: 12, color: 'var(--color-neutral-700)' }}>
              {sMonthLabels.map((t) => <span key={t.k} style={{ position: 'absolute', top: 6, left: t.left, transform: 'translateX(-50%)', whiteSpace: 'nowrap' }}>{t.label}</span>)}
            </div>
          </div>
          <div style={{ ...legendRow, marginTop: 12 }}>
            {typeLegend.map((l) => (
              <div key={l.k} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <span style={{ width: 10, height: 10, display: 'block', borderRadius: '50%', background: l.bg }} />{l.label}
              </div>
            ))}
            <span style={{ color: 'var(--color-neutral-600)' }}>Larger dot = more bales on the invoice</span>
          </div>
        </Blueprint>

        {/* ₹ per kg by month */}
        <Blueprint className="sheet" style={{ padding: '22px 24px' }}>
          <PanelHead title="Average ₹ per kg of cotton, month by month" right={
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, color: 'var(--color-neutral-600)' }}>Weighted by kg · ex-GST, incl. CCI charges</span>
              <div className="no-print seg">
                {([['all', 'All'], ['CCI', 'CCI'], ['Private', 'Pvt'], ['Group', 'Mohan group']] as const).map(([k, l]) => (
                  <button key={k} type="button" className={`btn ${kSrc === k ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => { setKSrc(k); setKHov(null) }}>{l}</button>
                ))}
              </div>
            </div>
          } />
          <div style={{ display: 'grid', gridTemplateColumns: '56px minmax(0,1fr)', gap: 8, marginTop: 22 }}>
            <div className="num" style={{ ...axisTxt, height: 260 }}>
              {kTicks.map((t) => <span key={t.pos} style={{ ...tickL, bottom: t.pos }}>{t.label}</span>)}
            </div>
            <div onMouseLeave={() => setKHov(null)} style={{ ...plot, height: 260 }}>
              {kTicks.map((t) => <span key={t.pos} style={{ ...gridLine, bottom: t.pos }} />)}
              <div style={{ position: 'absolute', inset: 0, display: 'flex' }}>
                {mAgg.map((o, i) => (
                  <div key={o.k} onMouseEnter={() => setKHov(i)} style={{ flex: '1 1 0', height: '100%', background: kHov === i ? HOVER_BG : 'transparent' }} />
                ))}
              </div>
              <svg viewBox="0 0 100 100" preserveAspectRatio="none"
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'visible', pointerEvents: 'none' }}>
                {kLines.map((l) => (
                  <polyline key={l.k} points={l.pts} fill="none" stroke={l.color} strokeWidth={l.w} strokeDasharray={l.dash} vectorEffect="non-scaling-stroke" />
                ))}
              </svg>
              {kDots.map((p, i) => (
                <span key={i} style={{
                  position: 'absolute', left: p.left, bottom: p.bottom, width: 8, height: 8, transform: 'translate(-50%,50%)',
                  background: p.color, border: '1px solid var(--color-bg)', pointerEvents: 'none',
                }} />
              ))}
              {kAllLabels.map((p, i) => (
                <span key={i} className="num" style={{
                  position: 'absolute', left: p.left, bottom: p.bottom, transform: 'translate(-50%,-10px)', fontSize: 11,
                  fontWeight: 600, whiteSpace: 'nowrap', background: 'var(--color-bg)', padding: '0 3px', pointerEvents: 'none',
                }}>{p.label}</span>
              ))}
              {kHovBox ? (
                <div style={{ ...tip, top: 8, left: kHovBox.left, transform: kHovBox.shift, minWidth: 230 }}>
                  <div style={{ fontWeight: 600 }}>{kHovBox.title}</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 6 }}>
                    {kHovBox.rows.map((r) => (
                      <div key={r.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ width: 10, height: 10, display: 'block', flex: 'none', background: r.color }} />
                        <span style={{ flex: 1, color: 'var(--color-neutral-700)' }}>{r.label}</span>
                        <span className="num" style={{ fontWeight: 600 }}>{r.v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '56px minmax(0,1fr)', gap: 8 }}>
            <div />
            <div style={{ display: 'flex', paddingTop: 6, fontSize: 12, color: 'var(--color-neutral-700)' }}>
              {months.map((b) => <span key={b.k} style={{ flex: '1 1 0', textAlign: 'center', whiteSpace: 'nowrap', fontWeight: b.fw }}>{b.label}</span>)}
            </div>
          </div>
          <div style={legendRow}>
            {kSeries.map((s) => (
              <div key={s.k} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <span style={{ width: 18, height: 0, display: 'block', borderTop: `2px ${s.style} ${s.color}` }} />{s.label}
              </div>
            ))}
          </div>
        </Blueprint>

        {/* Suppliers */}
        <Blueprint className="sheet" style={{ padding: '22px 24px 18px' }}>
          <PanelHead title="Suppliers" hint="Click a supplier to filter the page" />
          <div className="scroll-x" style={{ marginTop: 14 }}>
            <table className="table" style={{ width: '100%', minWidth: 960 }}>
              <thead>
                <tr>
                  <th>Supplier</th><th>Station</th><th style={R}>Invoices</th><th style={R}>Bales</th>
                  <th style={{ width: 150 }}>Share of bales</th><th style={R}>Avg ₹/candy</th><th style={R}>Range ₹/candy</th>
                  <th style={R}>Mill − party wt</th><th style={R}>Total value</th>
                </tr>
              </thead>
              <tbody>
                {sups.map((o) => {
                  const share = PT ? o.a.bales / PT * 100 : 0
                  const short = o.a.party > 0 && o.a.diff / o.a.party < -0.002
                  return (
                    <tr key={o.s} className="clickable" onClick={() => { setSupplier(supplier === o.s ? 'all' : o.s); setShowAll(false) }}
                      style={{ cursor: 'pointer', background: supplier === o.s ? 'color-mix(in oklch, var(--color-accent-200) 45%, transparent)' : 'transparent' }}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ width: 10, height: 10, display: 'block', flex: 'none', background: T_BG[o.type] }} />
                          <span style={{ fontWeight: 600 }}>{o.s}</span>
                          <span className="tag tag-outline" style={{ fontSize: 10 }}>{o.type}</span>
                        </div>
                      </td>
                      <td style={{ color: 'var(--color-neutral-700)', maxWidth: 220 }}>{o.st}</td>
                      <td className="num" style={R}>{num(o.a.n)}</td>
                      <td className="num" style={{ ...R, fontWeight: 600 }}>{num(o.a.bales)}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ flex: 1, height: 8, background: 'var(--color-neutral-200)' }}>
                            <div style={{ height: '100%', width: share + '%', background: T_BG[o.type] }} />
                          </div>
                          <span className="num" style={{ fontSize: 11, minWidth: 34, textAlign: 'right' }}>{num(share, 1)}%</span>
                        </div>
                      </td>
                      <td className="num" style={{ ...R, fontWeight: 600 }}>₹{num(o.a.candy)}</td>
                      <td className="num" style={{ ...R, color: 'var(--color-neutral-700)' }}>
                        {Math.round(o.lo) === Math.round(o.hi) ? '–' : '₹' + num(o.lo / 1000, 1) + 'k – ' + num(o.hi / 1000, 1) + 'k'}
                      </td>
                      <td className="num" style={{ ...R, color: short ? 'var(--color-text)' : 'var(--color-neutral-700)' }}>{diffTxt(o.a)}</td>
                      <td className="num" style={{ ...R, fontWeight: 600, whiteSpace: 'nowrap' }}>{cr(o.a.val)}</td>
                    </tr>
                  )
                })}
                <tr style={{ borderTop: '2px solid var(--color-neutral-500)' }}>
                  <td style={{ fontWeight: 700 }}>Total</td><td />
                  <td className="num" style={{ ...R, fontWeight: 700 }}>{num(supTot.n)}</td>
                  <td className="num" style={{ ...R, fontWeight: 700 }}>{num(supTot.bales)}</td>
                  <td />
                  <td className="num" style={{ ...R, fontWeight: 700 }}>₹{num(supTot.candy)}</td>
                  <td />
                  <td className="num" style={{ ...R, fontWeight: 700 }}>{diffTxt(supTot)}</td>
                  <td className="num" style={{ ...R, fontWeight: 700, whiteSpace: 'nowrap' }}>{cr(supTot.val)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div style={{ fontSize: 12, color: 'var(--color-neutral-600)', marginTop: 10 }}>Total value = invoice value incl. GST.</div>
        </Blueprint>

        {/* Invoice register */}
        <Blueprint className="sheet" style={{ padding: '22px 24px 18px' }}>
          <PanelHead title="Invoice register" hint={`${num(reg.length)} invoices · newest first`} />
          <div className="scroll-x" style={{ marginTop: 14 }}>
            <table className="table" style={{ width: '100%', minWidth: 1100 }}>
              <thead>
                <tr>
                  <th>Received</th><th>Supplier</th><th>Station</th><th>Invoice no.</th><th>Party lot</th>
                  <th style={R}>Bales</th><th style={R}>Kgs</th><th style={R}>₹/candy</th><th style={R}>₹/kg</th>
                  <th style={R}>Invoice value</th><th style={R}>Mill − party wt</th>
                </tr>
              </thead>
              <tbody>
                {regRows.map((r, i) => (
                  <tr key={r.inv + r.lot + i}>
                    <td className="num" style={{ whiteSpace: 'nowrap' }}>{dLabel(r.d)}</td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        {r.sup}{r.trading ? <span className="tag tag-neutral" style={{ fontSize: 10 }}>Trading</span> : null}
                      </div>
                    </td>
                    <td style={{ color: 'var(--color-neutral-700)' }}>{r.st}</td>
                    <td className="num" style={{ color: 'var(--color-neutral-700)' }}>{r.inv || '–'}</td>
                    <td className="num" style={{ color: 'var(--color-neutral-700)' }}>{r.lot || '–'}</td>
                    <td className="num" style={R}>{num(r.bales)}</td>
                    <td className="num" style={R}>{num(r.kg)}</td>
                    <td className="num" style={{ ...R, fontWeight: 600 }}>₹{num(candyOf(r))}</td>
                    <td className="num" style={R}>{num(r.taxable / r.kg, 2)}</td>
                    <td className="num" style={R}>₹{num(r.invVal / 1e5, 2)} L</td>
                    <td className="num" style={{ ...R, color: r.diff !== null && r.diff < -100 ? 'var(--color-text)' : 'var(--color-neutral-700)' }}>
                      {r.diff === null ? '–' : (r.diff > 0 ? '+' : '') + num(r.diff)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {reg.length > LIM ? (
            <div className="no-print" style={{ display: 'flex', justifyContent: 'center', marginTop: 14 }}>
              <button className="btn btn-secondary" type="button" onClick={() => setShowAll(!showAll)}>
                {showAll ? `Show latest ${LIM}` : `Show all ${reg.length} invoices`}
              </button>
            </div>
          ) : null}
          <div className="pretty" style={{ fontSize: 12, color: 'var(--color-neutral-700)', marginTop: 16, lineHeight: 1.55, maxWidth: '120ch' }}>
            Source: {data?.source ?? 'cotton purchase bills'}. Months follow the bill sheet they were entered in.
            Rates are ex-GST and include CCI handling charges; landed cost adds freight only where the sheet
            records it. Mill − party weight is the mill weighbridge minus the supplier's invoiced weight;
            negative means the load came in short. Rows tagged Trading are trading purchases and are included
            in totals.
          </div>
        </Blueprint>
      </main>
    </>
  )
}

/** Load a newer bills workbook: every bill month it carries is replaced.
 *  A viewer sees only what is on file. */
function UploadLine({ onLoaded, source }: { onLoaded: () => void; source?: string }) {
  const canUpload = useCan('upload')
  // An editor hands files in on the Data loads page instead.
  const loadElsewhere = useCan('load') && !canUpload
  const ref = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<{ text: string; bad?: boolean } | null>(null)

  const upload = async (files: FileList | null) => {
    const file = files?.[0]
    if (!file) return
    setBusy(true)
    setMsg(null)
    try {
      const r = await purchases.upload(file)
      setMsg({ text: `${r.source}: ${num(r.rows)} bill lines over ${r.months.length} months; ${num(r.rows_replaced)} replaced.` })
      onLoaded()
    } catch (e: any) {
      setMsg({ text: e?.message ?? String(e), bad: true })
    } finally {
      setBusy(false)
      if (ref.current) ref.current.value = ''
    }
  }

  return (
    <div className="no-print" style={{
      display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginLeft: 'auto',
      fontSize: 12, color: 'var(--color-neutral-700)',
    }}>
      {source ? <span>{source}</span> : null}
      {canUpload ? (
        <label className="tag tag-outline" style={{ cursor: busy ? 'default' : 'pointer' }}>
          {busy ? 'Reading…' : 'Load a newer bills workbook'}
          <input ref={ref} type="file" accept=".xlsx" disabled={busy}
            onChange={(e) => upload(e.target.files)} style={{ display: 'none' }} />
        </label>
      ) : null}
      {loadElsewhere ? <LoadElsewhere /> : null}
      {msg ? <span style={{ color: msg.bad ? 'var(--color-bad)' : 'var(--color-good)' }}>{msg.text}</span> : null}
    </div>
  )
}
